/**
 * Cascade dedupe: given a flat list of DocumentTemplates that may include
 * multiple entries per `key` at different owner_levels, return one entry
 * per key — the most-specific leaf applicable to the viewing scope.
 *
 * Preference order depends on scope:
 *   system view    → only system
 *   office view    → office > system
 *   PM view        → program_manager > office > system
 *   program view   → program > program_manager > office > system
 */
import type { DocumentTemplate, OwnerLevel, Scope } from './types'

const RANK: Record<OwnerLevel, number> = {
  system: 0,
  office: 1,
  program_manager: 2,
  program: 3,
}

function scopeMax(scope: Scope): number {
  return RANK[scope.level]
}

export function dedupeByKeyForScope(
  templates: DocumentTemplate[],
  scope: Scope,
): DocumentTemplate[] {
  const maxRank = scopeMax(scope)
  const byKey = new Map<string, DocumentTemplate>()
  for (const tpl of templates) {
    const tplRank = RANK[tpl.owner_level]
    if (tplRank > maxRank) continue // e.g. office-level template shouldn't show in system view
    const existing = byKey.get(tpl.key)
    if (!existing || tplRank > RANK[existing.owner_level]) {
      byKey.set(tpl.key, tpl)
    }
  }
  return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name))
}

/** Whether the given template is owned at (or below) the current scope — i.e.
 * whether editing it directly is safe, vs. needing an auto-customize first. */
export function isOwnedAtScope(tpl: DocumentTemplate, scope: Scope, ctx: { userId?: number; officeId?: number; programId?: number }): boolean {
  if (tpl.owner_level !== scope.level) return false
  if (scope.level === 'system') return true
  if (scope.level === 'office') return tpl.owner_office === (ctx.officeId ?? scope.ownerId)
  if (scope.level === 'program_manager') return tpl.owner_user === (ctx.userId ?? scope.ownerId)
  if (scope.level === 'program') return tpl.owner_program === (ctx.programId ?? scope.ownerId)
  return false
}
